'use client'

import React, { useState, useEffect } from 'react'
import type { Event, Tag, EventType } from '@/payload-types'
import { Media as MediaType } from '@/payload-types'
import { Card } from '@/ui/components/Card'
import { CardType } from '@/ui/components/Card/types'
import { Icon } from '@/ui/atoms/Icon'
import { format, addMonths, subMonths } from 'date-fns'
import { TypedLocale } from 'payload'
import { useTranslations } from 'next-intl'
import { Button, ButtonContent } from '@/ui/atoms/Button'
import { fetchMonthEvents } from '@/ui/blocks/EventArchive/action'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'
import Link from 'next/link'

type AuthorData = {
  name: string
  image?: MediaType | string | null
  verified?: boolean
}

type EventsByMonth = {
  [key: string]: Event[]
}

interface Props {
  initialEvents: Event[]
  locale: TypedLocale
}

const EmptyState = ({ month }: { month: Date }) => {
  const t = useTranslations('public')
  const monthStr = format(month, 'MMMM yyyy')

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <h3 className="text-xl font-semibold mb-2">{t('no-events-found')}</h3>
      <p className="text-gray-500 max-w-md">{t('no-events-for-month', { month: monthStr })}</p>
    </div>
  )
}

export const EventCarousel: React.FC<Props> = ({ initialEvents, locale }) => {
  const t = useTranslations('public')
  const [currentMonthDate, setCurrentMonthDate] = useState(new Date())
  const [eventsByMonth, setEventsByMonth] = useState<EventsByMonth>({})
  const [isLoading, setIsLoading] = useState(false)

  // Initialize with current month's events
  useEffect(() => {
    const currentMonthKey = format(new Date(), 'yyyy-MM')
    setEventsByMonth({
      [currentMonthKey]: initialEvents,
    })
  }, [initialEvents])

  const currentMonthKey = format(currentMonthDate, 'yyyy-MM')
  const monthKeys = Object.keys(eventsByMonth).sort()
  const currentMonthIndex = monthKeys.indexOf(currentMonthKey)

  const loadEventsForMonth = async (date: Date) => {
    const monthKey = format(date, 'yyyy-MM')

    // If we already have events for this month, no need to fetch
    if (eventsByMonth[monthKey]) {
      return
    }

    setIsLoading(true)
    try {
      const events = await fetchMonthEvents(locale, monthKey)
      setEventsByMonth((prev) => ({
        ...prev,
        [monthKey]: events,
      }))
    } catch (error) {
      console.error('Failed to fetch events:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePrevMonth = async () => {
    const prevDate = subMonths(currentMonthDate, 1)
    await loadEventsForMonth(prevDate)
    setCurrentMonthDate(prevDate)
    sendGAEvent('event', 'eventsPrevClicked')
    trackEvent('eventsPrevClicked')
  }

  const handleNextMonth = async () => {
    const nextDate = addMonths(currentMonthDate, 1)
    await loadEventsForMonth(nextDate)
    setCurrentMonthDate(nextDate)
    sendGAEvent('event', 'eventsNextClicked')
    trackEvent('eventsNextClicked')
  }

  // Pre-fetch adjacent months
  useEffect(() => {
    const prevDate = subMonths(currentMonthDate, 1)
    const nextDate = addMonths(currentMonthDate, 1)
    loadEventsForMonth(prevDate)
    loadEventsForMonth(nextDate)
  }, [currentMonthDate])

  if (!eventsByMonth[currentMonthKey] && !isLoading) {
    return null
  }

  const currentMonthEvents = eventsByMonth[currentMonthKey] || []

  return (
    <div className="relative">
      {/* Month Navigation */}
      <div className="flex justify-between items-center mb-6 lg:mb-10">
        <Button variant="secondary" size="md" onClick={handlePrevMonth} aria-label={t('previous')}>
          <ButtonContent showIconLeft iconLeft="arrow-left-line" />
        </Button>

        <div className="text-title-S-medium text-text-base">
          {format(currentMonthDate, 'MMMM yyyy')}
        </div>

        <Button variant="secondary" size="md" onClick={handleNextMonth} aria-label={t('next')}>
          <ButtonContent showIconRight iconRight="arrow-right-line" />
        </Button>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bg-brand-blue"></div>
        </div>
      )}

      {/* Events Grid or Empty State */}
      <div className="overflow-hidden">
        {currentMonthEvents.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {currentMonthEvents.map((event) => {
              const eventType = event.eventType as EventType | undefined
              return (
                <Card
                  target={'_blank'}
                  type={CardType.Event}
                  key={event.id}
                  title={event.title}
                  startDate={event?.startDate ?? ''}
                  endDate={event?.endDate ?? ''}
                  location={event?.location ?? ''}
                  category={
                    eventType && typeof eventType === 'object'
                      ? {
                          title: eventType.title,
                          colorScheme: eventType.colorScheme,
                        }
                      : undefined
                  }
                  labels={event?.tags?.map((tag) => (tag as Tag)?.title)}
                  isRecommended={event.isRecommended ?? false}
                  recommendedBy={event.recommendedBy as AuthorData[]}
                  recommendedWhy={event.isRecommended ? event.recommendedWhy : undefined}
                  longRecommendation={event.isRecommended ? event.longRecommendation : undefined}
                  href={event.link?.link?.url}
                  image={event.image}
                  description={event.description}
                  organizer={event.organizedBy}
                  organizerIsLinkable={event.organizerIsLinkable}
                  organizerLinkType={event.organizerLinkType}
                  organizerVendor={event.organizerVendor}
                  organizerLink={event.organizerLink}
                  language={event.languages?.join(', ')}
                  link={event.link}
                  showCTAButton={event.showCTAButton}
                  secondLink={event.secondLink}
                  showSecondCTAButton={event.showSecondCTAButton}
                  locale={locale}
                />
              )
            })}
          </div>
        ) : (
          <EmptyState month={currentMonthDate} />
        )}
      </div>

      {/* Show All Events Button */}
      <div className="flex justify-center mt-8">
        <Button asChild variant="secondary" size="md">
          <Link href={`/${locale}/events`}>
            <ButtonContent label={t('show-all-events')} />
          </Link>
        </Button>
      </div>
    </div>
  )
}
